/**
 * Export utilities for statistics data
 * Client-side CSV generation without external dependencies
 */

const downloadCSV = (rows, filename) => {
  const content = rows
    .map(r => r.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n')
  // BOM for correct encoding in Excel
  const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${filename}-${new Date().toISOString().split('T')[0]}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Export list view statistics to CSV
 * @param {Array} stats - array of manager stats objects
 * @param {Array} managers - array of manager user objects (with id, name/fullName, email)
 */
export const exportListViewCSV = (stats, managers) => {
  const managerMap = Object.fromEntries(
    managers.map(m => [m.value || m.id, m])
  )

  const headers = [
    'Manager', 'Email', 'Total Talents',
    'Candidates', 'Employees', 'Presales',
    '-> Employees (from cand.)', '-> Presales (from cand.)'
  ]

  const rows = stats.map(item => {
    const mgr = managerMap[item.manager_id] || {}
    const managerUser = mgr.user || mgr
    const name = managerUser.fullName || managerUser.name || mgr.label || item.manager_id
    const email = managerUser.email || mgr.email || ''
    const managerData = item.manager
    const managerInfo = managerData?.user || managerData || {}
    const displayName = managerInfo.fullName || managerInfo.name || name
    const displayEmail = managerInfo.email || email

    return [
      displayName,
      displayEmail,
      item.total_talents || 0,
      item.by_type?.candidate || 0,
      item.by_type?.employee || 0,
      item.by_type?.presale || 0,
      item.conversions?.to_employee || 0,
      item.conversions?.to_presale || 0
    ]
  })

  const totals = [
    'TOTAL', '',
    rows.reduce((s, r) => s + Number(r[2]), 0),
    rows.reduce((s, r) => s + Number(r[3]), 0),
    rows.reduce((s, r) => s + Number(r[4]), 0),
    rows.reduce((s, r) => s + Number(r[5]), 0),
    rows.reduce((s, r) => s + Number(r[6]), 0),
    rows.reduce((s, r) => s + Number(r[7]), 0)
  ]

  downloadCSV([headers, ...rows, totals], 'statistics-list')
}

/**
 * Export matrix view statistics to CSV
 * @param {Array} matrixData - stats enriched with .days map { dateStr: count }
 * @param {Object} matrixColumnTotals - { dateStr: count } column totals
 * @param {number} matrixGrandTotal - overall grand total
 * @param {Array} dateColumns - array of { key, label, dateStr } objects
 */
export const exportMatrixViewCSV = (matrixData, matrixColumnTotals, matrixGrandTotal, dateColumns) => {
  const headers = [
    'Manager', 'Email',
    ...dateColumns.map(col => col.dateStr),
    'TOTAL'
  ]

  const rows = matrixData.map(item => {
    const managerData = item.manager
    const managerInfo = managerData?.user || managerData || {}
    const name = managerInfo.fullName || managerInfo.name || item.manager_id
    const email = managerInfo.email || ''
    const dayCounts = dateColumns.map(col => item.days?.[col.dateStr] || 0)

    return [name, email, ...dayCounts, item.total_talents || 0]
  })

  const totalRow = [
    'TOTAL', '',
    ...dateColumns.map(col => matrixColumnTotals[col.dateStr] || 0),
    matrixGrandTotal
  ]

  downloadCSV([headers, ...rows, totalRow], 'statistics-matrix')
}

/**
 * Export custom report to CSV
 * @param {Array} rows - array of report rows
 * @param {Array} dimensions - array of dimension keys
 * @param {Array} dimensionOptions - array of dimension option objects { value, label }
 * @param {Function} formatFn - function to format dimension values (dimKey, value, row) => string
 */
export const exportCustomReportCSV = (rows, dimensions, dimensionOptions, formatFn) => {
  if (!rows || rows.length === 0) return

  // Headers - dimension labels + Count
  const headers = [
    ...dimensions.map(d => dimensionOptions.find(opt => opt.value === d)?.label || d),
    'Count'
  ]

  // Data rows
  const dataRows = rows.map(row => [
    ...dimensions.map(d => {
      const val = row[d]
      // Format for CSV (plain text, no JSX)
      if (d === 'is_active') return val === true || val === 'true' ? 'Active' : 'Inactive'
      if (d === 'created_month' || d === 'created_year') {
        if (!val) return ''
        try {
          return new Date(val).toLocaleDateString('en-US',
            d === 'created_month' ? { year: 'numeric', month: 'short' } : { year: 'numeric' }
          )
        } catch { return val }
      }
      // For status, only the name (not color circle)
      if (d === 'status') return val || 'No Status'
      // Other dimensions
      return formatFn ? formatFn(d, val, row) : (val ?? '')
    }),
    row.count || 0
  ])

  // Total row
  const totalCount = dataRows.reduce((sum, r) => sum + Number(r[r.length - 1] || 0), 0)
  const totalRow = [
    'TOTAL',
    ...Array(dimensions.length - 1).fill(''),
    totalCount
  ]

  downloadCSV([headers, ...dataRows, totalRow], 'custom-report')
}

/**
 * Export hired stats with per-talent rows
 * @param {Array} stats - manager stats from list view
 * @param {Function} fetchDetailsFn - (managerId, params) => Promise<response>
 * @param {Object} filters - { startDate, endDate }
 */
export const exportHiredWithTalentsCSV = async (stats, fetchDetailsFn, filters) => {
  const rows = []
  const origin = window.location.origin
  rows.push(['Manager', 'Manager Email', 'Talent Name', 'Talent Type', 'Department', 'Profession', 'From Status', 'Hired Date', 'Profile URL'])

  for (const item of stats) {
    const managerInfo = item.manager?.user || item.manager || {}
    const managerName = managerInfo.fullName || managerInfo.name || item.manager_id
    const managerEmail = managerInfo.email || ''

    const response = await fetchDetailsFn(item.manager_id, { ...filters, page: 1, limit: 9999 })
    const talents = response?.data?.recent_hired || []

    if (talents.length === 0) {
      rows.push([managerName, managerEmail, '—', '', '', '', '', '', ''])
    } else {
      for (const t of talents) {
        rows.push([
          managerName, managerEmail,
          t.full_name || '',
          t.talent_type || '',
          t.department || '',
          t.profession || '',
          t.from_status || '',
          t.hired_at ? new Date(t.hired_at).toLocaleDateString('uk-UA') : '',
          t.id ? `${origin}/talents/${t.id}` : ''
        ])
      }
    }
  }

  downloadCSV(rows, 'hired-with-talents')
}

/**
 * Export started stats with per-talent rows
 */
export const exportStartedWithTalentsCSV = async (stats, fetchDetailsFn, filters) => {
  const rows = []
  const origin = window.location.origin
  rows.push(['Manager', 'Manager Email', 'Talent Name', 'Talent Type', 'Department', 'Profession', 'From Status', 'Start Date', '2-Week Status', 'Profile URL'])

  for (const item of stats) {
    const managerInfo = item.manager?.user || item.manager || {}
    const managerName = managerInfo.fullName || managerInfo.name || item.manager_id
    const managerEmail = managerInfo.email || ''

    const response = await fetchDetailsFn(item.manager_id, { ...filters, page: 1, limit: 9999 })
    const talents = response?.data?.recent_started || []

    if (talents.length === 0) {
      rows.push([managerName, managerEmail, '—', '', '', '', '', '', '', ''])
    } else {
      for (const t of talents) {
        rows.push([
          managerName, managerEmail,
          t.full_name || '',
          t.talent_type || '',
          t.department || '',
          t.profession || '',
          t.from_status || '',
          t.started_at ? new Date(t.started_at).toLocaleDateString('uk-UA') : '',
          t.churned ? 'Churned' : 'Retained',
          t.id ? `${origin}/talents/${t.id}` : ''
        ])
      }
    }
  }

  downloadCSV(rows, 'started-with-talents')
}

/**
 * Export added (talent created) stats with per-talent rows
 */
export const exportAddedWithTalentsCSV = async (stats, fetchDetailsFn, filters) => {
  const rows = []
  const origin = window.location.origin
  rows.push(['Manager', 'Manager Email', 'Talent Name', 'Talent Type', 'Department', 'Profession', 'Added Date', 'Profile URL'])

  for (const item of stats) {
    const managerInfo = item.manager?.user || item.manager || {}
    const managerName = managerInfo.fullName || managerInfo.name || item.manager_id
    const managerEmail = managerInfo.email || ''

    const response = await fetchDetailsFn(item.manager_id, { ...filters, page: 1, limit: 9999 })
    const talents = response?.data?.recent_talents || []

    if (talents.length === 0) {
      rows.push([managerName, managerEmail, '—', '', '', '', '', ''])
    } else {
      for (const t of talents) {
        rows.push([
          managerName, managerEmail,
          t.full_name || '',
          t.talent_type || '',
          t.department || '',
          t.profession || '',
          t.created_at ? new Date(t.created_at).toLocaleDateString('uk-UA') : '',
          t.id ? `${origin}/talents/${t.id}` : ''
        ])
      }
    }
  }

  downloadCSV(rows, 'added-with-talents')
}

/**
 * Export video stats with per-video rows
 * @param {Array} stats - manager stats from list view
 * @param {Function} fetchDetailsFn - (managerId, params) => Promise<response>
 * @param {Object} filters - { startDate, endDate }
 */
export const exportVideoWithTalentsCSV = async (stats, fetchDetailsFn, filters) => {
  const rows = []
  const origin = window.location.origin
  rows.push(['Manager', 'Manager Email', 'File Name', 'Talent Name', 'Department', 'Profession', 'Profile URL', 'Uploaded Date'])

  for (const item of stats) {
    const managerInfo = item.manager?.user || item.manager || {}
    const managerName = managerInfo.fullName || managerInfo.name || item.manager_id
    const managerEmail = managerInfo.email || ''

    const response = await fetchDetailsFn(item.manager_id, { ...filters, page: 1, limit: 9999 })
    const videos = response?.data?.recent_videos || []

    for (const v of videos) {
      rows.push([
        managerName,
        managerEmail,
        v.file_name || '',
        v.talent_name,
        v.department || '',
        v.profession || '',
        v.talent_id ? `${origin}/talents/${v.talent_id}` : '',
        v.uploaded_at ? new Date(v.uploaded_at).toLocaleDateString('uk-UA') : ''
      ])
    }
  }

  downloadCSV(rows, 'video-with-talents')
}

/**
 * Export period comparison to CSV
 * @param {Object} comparisonData - Full comparison response { period1, period2, comparison }
 */
export const exportPeriodComparisonCSV = (comparisonData) => {
  if (!comparisonData) return

  const { period1, period2, comparison } = comparisonData

  const formatChange = (changeObj) => {
    if (!changeObj) return ''
    const sign = changeObj.absolute >= 0 ? '+' : ''
    return `${sign}${changeObj.absolute} (${sign}${changeObj.percentage}%)`
  }

  // SUMMARY SECTION
  const summaryHeaders = [
    'Metric',
    period1.label || 'Period 1',
    period2.label || 'Period 2',
    'Change (Absolute)',
    'Change (%)'
  ]

  const summaryRows = [
    [
      'Total Talents',
      period1.total_talents || 0,
      period2.total_talents || 0,
      comparison.total_talents?.absolute || 0,
      comparison.total_talents?.percentage || 0
    ],
    [
      'Candidates',
      period1.by_type?.candidate || 0,
      period2.by_type?.candidate || 0,
      comparison.by_type?.candidate?.absolute || 0,
      comparison.by_type?.candidate?.percentage || 0
    ],
    [
      'Employees',
      period1.by_type?.employee || 0,
      period2.by_type?.employee || 0,
      comparison.by_type?.employee?.absolute || 0,
      comparison.by_type?.employee?.percentage || 0
    ],
    [
      'Presales',
      period1.by_type?.presale || 0,
      period2.by_type?.presale || 0,
      comparison.by_type?.presale?.absolute || 0,
      comparison.by_type?.presale?.percentage || 0
    ]
  ]

  // BY MANAGER SECTION
  const managerHeaders = [
    'Manager',
    'Email',
    `${period1.label || 'Period 1'} - Total`,
    `${period2.label || 'Period 2'} - Total`,
    'Change (Absolute)',
    'Change (%)'
  ]

  const managerRows = (comparison.by_manager || []).map(item => {
    const managerData = item.manager
    const managerInfo = managerData?.user || managerData || {}
    const name = managerInfo.fullName || managerInfo.name || item.manager_id
    const email = managerInfo.email || ''

    // Find corresponding data in period1 and period2
    const p1Manager = period1.by_manager?.find(m => m.manager_id === item.manager_id)
    const p2Manager = period2.by_manager?.find(m => m.manager_id === item.manager_id)

    return [
      name,
      email,
      p1Manager?.total || 0,
      p2Manager?.total || 0,
      item.total?.absolute || 0,
      item.total?.percentage || 0
    ]
  })

  // Combine all sections
  const allRows = [
    ['SUMMARY'],
    summaryHeaders,
    ...summaryRows,
    [], // Empty row separator
    ['BY MANAGER'],
    managerHeaders,
    ...managerRows
  ]

  downloadCSV(allRows, 'period-comparison')
}
